import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent, type ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/cn";
import { useTranslation } from "@/i18n/I18nProvider";

/** A horizontal move past this distance (px), wider than tall, is a swipe. */
const SWIPE_DISTANCE = 48;
const FADE_S = 0.22;

/** Decoded once per URL per session, so a revisit swaps instantly. */
const decoded = new Map<string, Promise<void>>();
function decode(src: string): Promise<void> {
  let p = decoded.get(src);
  if (!p) {
    const img = new Image();
    img.decoding = "async";
    img.src = src;
    // A failed decode still resolves: the <img> then shows whatever the
    // browser can, instead of the gallery getting stuck on the old photo.
    p = img.decode().catch(() => undefined);
    decoded.set(src, p);
  }
  return p;
}

/**
 * The venue detail photo carousel (venue-detail-offers.md Rule 3).
 *
 * Why it never flashes black: the next photo is decoded BEFORE it mounts, and
 * it fades in on top of the current one, which stays painted underneath until
 * the fade ends. (The old `AnimatePresence mode="wait"` faded the current photo
 * out first and mounted an undecoded one after, so the dark container showed
 * through for a few frames.) Only the two neighbours are preloaded (egress).
 * Swipe sideways on touch (plain pointer events, so the area keeps
 * `touch-action: pan-y pinch-zoom`: vertical scroll and pinch-zoom, DEC1,
 * stay with the browser), arrows and dots everywhere.
 */
export function DetailGallery({
  images,
  alt,
  className,
  children,
}: {
  images: string[];
  alt: string;
  className?: string;
  /** Overlay content (the name/neighbourhood poster), drawn above the photos. */
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const count = images.length;
  // `shown` = the photo on top; `under` = the one it is fading in over.
  const [shown, setShown] = useState(0);
  const [under, setUnder] = useState<number | null>(null);
  const want = useRef(0);
  const start = useRef<{ x: number; y: number } | null>(null);

  function show(next: number) {
    if (count < 2) return;
    const i = ((next % count) + count) % count;
    want.current = i;
    const src = images[i];
    if (!src) return;
    void decode(src).then(() => {
      // A faster tap already asked for another photo: that one wins.
      if (want.current !== i) return;
      setShown((current) => {
        if (current !== i) setUnder(current);
        return i;
      });
    });
  }

  // Warm the neighbours of whatever is on screen.
  useEffect(() => {
    if (count < 2) return;
    for (const d of [1, -1]) {
      const src = images[(shown + d + count) % count];
      if (src) void decode(src);
    }
  }, [shown, count, images]);

  function onPointerUp(e: PointerEvent) {
    const s = start.current;
    start.current = null;
    if (!s || !e.isPrimary) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (Math.abs(dx) < SWIPE_DISTANCE || Math.abs(dx) < Math.abs(dy)) return;
    show(dx < 0 ? shown + 1 : shown - 1);
  }

  return (
    <div className={cn("relative w-full overflow-hidden bg-zinc-900 light:bg-zinc-100", className)}>
      <div
        className="absolute inset-0"
        style={{ touchAction: "pan-y pinch-zoom" }}
        onPointerDown={(e) => {
          if (e.isPrimary) start.current = { x: e.clientX, y: e.clientY };
        }}
        onPointerUp={onPointerUp}
        onPointerCancel={() => (start.current = null)}
      >
        {/* Keyed by photo index, so the photo that becomes the bottom layer is
            the SAME element that was on screen (a new key would remount it
            blank and repaint it: the old black flash). */}
        {(under !== null && under !== shown ? [under, shown] : [shown]).map((i) => (
          <PhotoLayer
            key={i}
            src={images[i] ?? ""}
            alt={i === shown ? alt : ""}
            fadeIn={i === shown && under !== null}
            duration={reduceMotion ? 0 : FADE_S}
            onShown={() => i === shown && setUnder(null)}
          />
        ))}
      </div>

      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/35 to-transparent" />
      {children}

      {count > 1 && (
        <>
          <button
            type="button"
            onClick={() => show(shown - 1)}
            aria-label={t("DETAIL_PREV_IMAGE")}
            className="absolute left-2 top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-white/20 light:border-black/15 bg-zinc-950/70 light:bg-white/70 text-zinc-100 light:text-zinc-900 backdrop-blur transition hover:border-white/60 light:hover:border-black/45 sm:left-3"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => show(shown + 1)}
            aria-label={t("DETAIL_NEXT_IMAGE")}
            className="absolute right-2 top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-white/20 light:border-black/15 bg-zinc-950/70 light:bg-white/70 text-zinc-100 light:text-zinc-900 backdrop-blur transition hover:border-white/60 light:hover:border-black/45 sm:right-3"
          >
            <ChevronRight className="h-5 w-5" aria-hidden />
          </button>
          {/* Each dot sits in a 24 px-tall button, easier to hit than the bare
              6 px dot. They stay narrow: the 40 px arrows above are the
              full-size control for the same action (WCAG 2.5.8 "equivalent"). */}
          <div className="absolute left-1/2 top-[3px] z-10 flex -translate-x-1/2 gap-1">
            {images.map((src, i) => (
              <button
                key={src}
                type="button"
                onClick={() => show(i)}
                aria-label={t("DETAIL_GO_IMAGE", { n: i + 1 })}
                aria-current={i === shown || undefined}
                className="group/dot flex h-6 items-center px-px"
              >
                <span
                  className={cn(
                    "block h-1.5 rounded-full transition-all",
                    i === shown
                      ? "w-5 bg-zinc-100 light:bg-zinc-900"
                      : "w-1.5 bg-zinc-100/40 light:bg-zinc-900/25 group-hover/dot:bg-zinc-100/70 light:group-hover/dot:bg-zinc-900/45",
                  )}
                  aria-hidden
                />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * One photo layer. A layer that fades in waits for ITS OWN element to have
 * loaded (the decode above warms the cache, but a fresh <img> can still take a
 * few frames), so the fade never runs over an empty element. A layer that is
 * already on screen stays at full opacity whatever its role becomes.
 */
function PhotoLayer({
  src,
  alt,
  fadeIn,
  duration,
  onShown,
}: {
  src: string;
  alt: string;
  fadeIn: boolean;
  duration: number;
  onShown: () => void;
}) {
  const ref = useRef<HTMLImageElement>(null);
  const [ready, setReady] = useState(!fadeIn);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el?.complete && el.naturalWidth > 0) setReady(true);
  }, []);
  return (
    <motion.img
      ref={ref}
      src={src}
      alt={alt}
      draggable={false}
      onLoad={() => setReady(true)}
      onError={() => setReady(true)}
      initial={fadeIn ? { opacity: 0 } : false}
      animate={{ opacity: ready ? 1 : 0 }}
      transition={{ duration: ready ? duration : 0, ease: "easeOut" }}
      onAnimationComplete={() => ready && onShown()}
      className="absolute inset-0 h-full w-full select-none object-cover"
    />
  );
}
